import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams, Link, useNavigate } from "react-router-dom";
import $api from "http/index";
import { listThemeMaterials } from "service/PlanBuilderService";
import { PageHeader, Card, EmptyState, Skeleton, Badge, DataTable, Icon, UnvButton } from "components";
import s from "./SubjectPage.module.scss";


export default function SubjectPage() {
  const { t } = useTranslation("common");
  const { id } = useParams();
  const navigate = useNavigate();
  const [subject, setSubject] = useState(null);
  const [themes, setThemes] = useState([]);
  const [materials, setMaterials] = useState({});
  const [assignments, setAssignments] = useState([]);
  const [tests, setTests] = useState([]);
  const [marks, setMarks] = useState([]);

  useEffect(() => {
    $api.get(`subject/${id}/`).then((r) => setSubject(r.data)).catch(() => {});
    $api.get(`themes/${id}/`).then((r) => {
      const list = r.data?.results || r.data || [];
      setThemes(list);
      list.forEach((th) => {
        listThemeMaterials({ theme: th.id })
          .then((rm) => setMaterials((prev) => ({ ...prev, [th.id]: rm.data?.results || rm.data || [] })))
          .catch(() => {});
      });
    }).catch(() => {});
    $api.get("assignments/assignments/", { params: { subject: id, page_size: 50 } })
      .then((r) => setAssignments(r.data?.results || r.data || [])).catch(() => {});
    $api.get("worktests/", { params: { subject: id, page_size: 50 } })
      .then((r) => setTests(r.data?.results || r.data || [])).catch(() => {});
    $api.get("academic/marks/", { params: { subject: id, page_size: 50 } })
      .then((r) => setMarks(r.data?.results || r.data || [])).catch(() => {});
  }, [id]);

  if (!subject) {
    return (
      <div className={s.page}>
        <Skeleton variant="page" />
      </div>
    )
  }

  const subtitle = (
    <span style={{ display: 'inline-flex', gap: 12, flexWrap: 'wrap' }}>
      <Badge variant="info">{t('subjectPage.credits_label')} {subject.credit || '—'}</Badge>
      <Badge variant="muted">{t('subjectPage.hours_label')} {subject.amount_hours || '—'}</Badge>
      <Badge variant="accent">{t('subjectPage.control_label')} {subject.control_form || '—'}</Badge>
    </span>
  )

  return (
    <div className={s.page}>
      <PageHeader
        icon={<Icon name="course" size={28} />}
        title={subject.name_subject}
        subtitle={subtitle}
        actions={
          <UnvButton variant="ghost" iconLeft="arrow-left" onClick={() => navigate(-1)}>
            {t('common.back', 'Назад')}
          </UnvButton>
        }
      />

      <div className={s.grid}>
        <section className={s.col}>
          <Card padding="md">
            <Card.Header title={t('subjectPage.themes_and_materials')} />
            <Card.Body>
              {themes.length === 0 ? (
                <EmptyState iconName="book" title={t('auto.subjectpage.no_themes', 'Нет тем')} />
              ) : themes.map((th) => (
                <details key={th.id} className={s.theme}>
                  <summary>{th.theme} <small>({th.theme_hours || 0} {t('subjectPage.hours_short')})</small></summary>
                  {th.description && <p className={s.desc}>{th.description}</p>}
                  <div className={s.materials}>
                    {(materials[th.id] || []).map((m) => (
                      <div key={m.id} className={s.material}>
                        <Badge variant="muted">{m.kind_display}</Badge>
                        <span className={s.matTitle}>{m.title}</span>
                        {m.file && <a href={m.file} target="_blank" rel="noreferrer">{t('subjectPage.download')}</a>}
                        {m.url && <a href={m.url} target="_blank" rel="noreferrer">{t('subjectPage.open')}</a>}
                      </div>
                    ))}
                    {(!materials[th.id] || materials[th.id].length === 0) && (
                      <div className={s.empty}>{t('subjectPage.no_materials')}</div>
                    )}
                  </div>
                </details>
              ))}
            </Card.Body>
          </Card>
        </section>

        <section className={s.col}>
          <Card padding="md">
            <Card.Header title={t('subjectPage.assignments_section')} />
            <Card.Body>
              {assignments.length === 0 ? (
                <EmptyState iconName="assignment" title={t('subjectPage.no_assignments')} />
              ) : assignments.map((a) => (
                <div key={a.id} className={s.card}>
                  <div className={s.cardTitle}>{a.title}</div>
                  <div className={s.cardMeta}>{t('subjectPage.due_inline', { date: a.due_at, max: a.max_points })}</div>
                  <Link to={`/assignments-list/?id=${a.id}`}>
                    <UnvButton variant="link" size="sm" iconRight="arrow-right">{t('subjectPage.open_arrow')}</UnvButton>
                  </Link>
                </div>
              ))}
            </Card.Body>
          </Card>

          <Card padding="md">
            <Card.Header title={t('subjectPage.tests_and_exams')} />
            <Card.Body>
              {tests.length === 0 ? (
                <EmptyState iconName="test" title={t('subjectPage.no_tests')} />
              ) : tests.map((tt) => (
                <div key={tt.id} className={s.card}>
                  <div className={s.cardTitle}>{tt.title}</div>
                  <div className={s.cardMeta}>{t('subjectPage.type_attempts_inline', { kind: tt.kind, attempts: tt.attempts_allowed })}</div>
                  <Link to={`/take-test/${tt.id}`}>
                    <UnvButton variant="link" size="sm" iconRight="arrow-right">{t('subjectPage.take_arrow')}</UnvButton>
                  </Link>
                </div>
              ))}
            </Card.Body>
          </Card>

          <Card padding="md">
            <Card.Header title={t('subjectPage.my_grades')} />
            <Card.Body>
              <DataTable
                columns={[
                  { key: 'created_at', label: t('subjectPage.col_date'), render: (v) => (v || '').slice(0, 10) },
                  { key: 'kind', label: t('subjectPage.col_kind') },
                  { key: 'value', label: t('subjectPage.col_score'), render: (_, row) => `${row.value} / ${row.max_value}` },
                ]}
                data={marks}
                searchable={false}
                emptyText={t('subjectPage.no_marks')}
              />
            </Card.Body>
          </Card>
        </section>
      </div>
    </div>
  );
}
